'use client';
import posthog, { PostHogConfig } from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { ReactNode, useEffect } from 'react';
import { dlog } from '../../dev';
import { capturePostHogEvent, POSTHOG_CONFIG } from './posthogConfig';
import { DriftWindow } from 'src/window/driftWindow';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import Env from 'src/environmentVariables/EnvironmentVariables';

const ENABLE_PERSISTENCE = false;
const DEV_TEST_FLAG_LOADING_DELAY = false;
const DISABLE_POSTHOG = true;

/**
 * Register static super properties that should be attached to every event.
 * Called at init and after posthog.reset() (which clears all registered properties).
 */
const registerStaticSuperProperties = () => {
	posthog.register({
		platform: 'web',
		env: Env.sdkEnv,
		user_agent: navigator.userAgent,
	});
};

const getOptionalPosthogParams = () => {
	const postHogParams: Partial<PostHogConfig> = {
		enable_recording_console_log: POSTHOG_CONFIG.consoleRecordingEnabled,
		session_recording: POSTHOG_CONFIG.sessionRecordingEnabled ? {} : undefined,
		capture_pageview: false, // Disabled — pageviews are captured manually in useRouterConfig on every SPA route change. Setting this to true would cause duplicate pageviews on initial load.
		capture_pageleave: true,
		disable_session_recording: POSTHOG_CONFIG.sessionRecordingEnabled
			? undefined
			: true,
	};

	return postHogParams;
};

const alreadyEnabledPosthog = { current: false };

export default function PHProvider({ children }: { children: ReactNode }) {
	const appEventEmitter = useDriftStore((s) => s.appEventEmitter);
	const setDriftStore = useDriftStore((s) => s.set);

	useEffect(() => {
		if (alreadyEnabledPosthog.current) return;
		if (!appEventEmitter) return;

		dlog(`posthog`, `posthog_enabled`);

		alreadyEnabledPosthog.current = true;

		if (DISABLE_POSTHOG) return;

		posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY ?? '', {
			api_host: 'https://s.drift.trade', // PostHog managed reverse proxy
			ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
			persistence: ENABLE_PERSISTENCE ? 'localStorage+cookie' : 'memory',
			loaded: (posthog) => {
				posthog.setPersonPropertiesForFlags({
					$current_url: window.location.href,
				});

				registerStaticSuperProperties();

				posthog.onFeatureFlags(async (flags) => {
					if (DEV_TEST_FLAG_LOADING_DELAY) {
						await new Promise((resolve) => setTimeout(resolve, 5_000));
					}

					dlog(`feature_flags`, `onFeatureFlags`, flags);

					appEventEmitter.emit('featureFlagsLoaded', flags);
				});

				// Set up global unhandled error handler
				window.onerror = (message, source, lineno, colno, error) => {
					if (posthog.isFeatureEnabled('LOG_CONSOLE_ERRORS')) {
						const authority: string | undefined =
							// @ts-ignore — walletContext is injected at runtime
							window?.walletContext?.publicKey?.toString();
						capturePostHogEvent('system_unhandled_error', {
							error_message: message,
							source,
							lineno,
							colno,
							stack: error?.stack,
							url: window.location.href,
							timestamp: new Date().toISOString(),
							authority,
						});
					}
					return false;
				};

				// Set up unhandled promise rejection handler
				window.onunhandledrejection = (event) => {
					if (posthog.isFeatureEnabled('LOG_CONSOLE_ERRORS')) {
						const authority: string | undefined =
							// @ts-ignore — walletContext is injected at runtime
							window?.walletContext?.publicKey?.toString();
						capturePostHogEvent('system_unhandled_promise_rejection', {
							error_message: event.reason?.message || String(event.reason),
							stack: event.reason?.stack,
							url: window.location.href,
							timestamp: new Date().toISOString(),
							authority,
						});
					}
				};
			},
			autocapture: false,
			...getOptionalPosthogParams(),
		});
	}, [appEventEmitter]);

	useEffect(() => {
		const handler = (enabled: boolean) => {
			if (enabled) {
				dlog(`posthog`, `session_recording_enabled`);
				posthog.startSessionRecording();
			} else {
				dlog(`posthog`, `session_recording_disabled`);
				posthog.stopSessionRecording();
			}
		};

		DriftWindow.devEventEmitter.on('set_session_recording', handler);

		return () => {
			DriftWindow.devEventEmitter.off('set_session_recording', handler);
		};
	}, []);

	// Listen for changes to event capture setting
	useEffect(() => {
		const handleSetEventCapture = (enabled: boolean) => {
			dlog(`posthog`, `set_event_capture: ${enabled}`);
			setDriftStore((s) => {
				s.postHog.enabled = enabled;
			});
		};

		DriftWindow.devEventEmitter.on('set_event_capture', handleSetEventCapture);

		// Cleanup function to remove listener
		return () => {
			DriftWindow.devEventEmitter.off(
				'set_event_capture',
				handleSetEventCapture
			);
		};
	}, []);

	return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}

export const POSTHOG_UTILS = {
	registerStaticSuperProperties,
};
